'use client';
/**
 * PUBLIER LE PLANNING (RQ, 02/10/2026).
 * ─────────────────────────────────────────────────────────────────────────
 * « Une seule notification quand le planning est prêt — poussée par le RH pour
 * un mois ou un trimestre — puis une notification par changement qui me
 * concerne, après ce push. » (Asma Ezzi, via la RQ.)
 *
 * Tant qu'une période n'est pas publiée, la RH construit en silence. Publier
 * prévient chaque personne affectée, UNE fois ; ensuite chaque changement sur
 * ces dates la prévient individuellement. Retirer une publication rend la
 * période silencieuse pour retravailler — le motif s'écrit, rien n'est effacé.
 *
 * Les règles vivent côté moteur (`planning-publication.ts`) ; l'écran ne fait
 * que choisir des dates et montrer ce qui est publié.
 */
import React from 'react';
import { card, inputStyle } from '@/components/ui';
import { fetchEcriture } from '@/lib/ecrire';
import { useGed } from '@/lib/store';

import { Bouton } from '@/shared/Bouton';
import { PiedActions } from '@/shared/PiedActions';
import { fmtDate } from '@/lib/meta';
type Publication = {
  id: string; du: string; au: string; le: string; par: string; parNom: string; prevenus?: number;
  active: boolean; libelle: string; retireeLe?: string; motifRetrait?: string;
};

const fr = (iso: string) => fmtDate(iso);
const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const premier = (a: number, m: number) => new Date(a, m, 1);
const dernier = (a: number, m: number) => new Date(a, m + 1, 0);

/** Les raccourcis : le mois prochain, le trimestre prochain… — ce qu'on publie en pratique. */
function raccourcis(): { cle: string; label: string; du: string; au: string }[] {
  const n = new Date();
  const a = n.getFullYear(); const m = n.getMonth();
  const t0 = Math.floor(m / 3) * 3;
  const mois = (dm: number) => { const d = new Date(a, m + dm, 1); return { du: iso(premier(d.getFullYear(), d.getMonth())), au: iso(dernier(d.getFullYear(), d.getMonth())) }; };
  const tri = (dt: number) => { const d = new Date(a, t0 + 3 * dt, 1); return { du: iso(premier(d.getFullYear(), d.getMonth())), au: iso(dernier(d.getFullYear(), d.getMonth() + 2)) }; };
  return [
    { cle: 'm0', label: 'Le mois en cours', ...mois(0) },
    { cle: 'm1', label: 'Le mois prochain', ...mois(1) },
    { cle: 't0', label: 'Le trimestre en cours', ...tri(0) },
    { cle: 't1', label: 'Le trimestre prochain', ...tri(1) },
  ];
}

export function PublicationPlanning({ onChange }: { onChange?: () => void }) {
  const { showToast } = useGed();
  const [liste, setListe] = React.useState<Publication[] | null>(null);
  const [ouvert, setOuvert] = React.useState(false);
  const [voirTout, setVoirTout] = React.useState(false);
  const rac = React.useMemo(() => raccourcis(), []);
  const [du, setDu] = React.useState(rac[1]!.du);
  const [au, setAu] = React.useState(rac[1]!.au);
  const [envoi, setEnvoi] = React.useState(false);
  const [retrait, setRetrait] = React.useState<{ id: string; motif: string } | null>(null);

  const charger = React.useCallback(async () => {
    const r = await fetch('/api/ged/ged/rh/planning/publications', { credentials: 'include' }).catch(() => null);
    setListe(r?.ok ? ((await r.json()).publications as Publication[]) : []);
  }, []);
  // eslint-disable-next-line react-hooks/set-state-in-effect -- lecture réseau à l'ouverture
  React.useEffect(() => { void charger(); }, [charger]);

  const publier = async () => {
    setEnvoi(true);
    const r = await fetchEcriture('/api/ged/ged/rh/planning/publier', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ du, au }),
    });
    const d = await r.json().catch(() => ({}));
    setEnvoi(false);
    if (!r.ok) { showToast(d?.error?.message || 'Publication refusée.', 'erreur'); return; }
    showToast(`Planning ${d.publication?.libelle || ''} publié — ${d.prevenus} personne(s) prévenue(s).`);
    setOuvert(false);
    await charger();
    onChange?.();
  };

  const retirer = async () => {
    if (!retrait) return;
    const r = await fetchEcriture(`/api/ged/ged/rh/planning/publications/${encodeURIComponent(retrait.id)}/retirer`, {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ motif: retrait.motif }),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) { showToast(d?.error?.message || 'Retrait refusé.', 'erreur'); return; }
    showToast('Publication retirée : cette période est de nouveau silencieuse.');
    setRetrait(null);
    await charger();
    onChange?.();
  };

  const actives = (liste || []).filter((p) => p.active);
  // Le détail (périodes, retrait, historique) ne s'ouvre qu'à la demande : la ligne dit déjà l'essentiel.
  const montrees = voirTout ? (liste || []) : [];
  const ordreOk = !!du && !!au && au >= du;

  return (
    /* COMPACT (RQ, 04/10/2026 — réorganisation du planning) : une ligne, pas un
       panneau. L'explication passe à l'infobulle ; le bouton est en contour — l'écran
       du planning n'a qu'une action principale, et ce n'est pas celle-ci. */
    <section aria-label="Publication du planning" style={{ ...card, padding: '6px 12px', margin: '0 0 10px', display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}
        title="Tant qu’une période n’est pas publiée, vos changements ne notifient personne. Publier prévient chaque personne affectée une fois ; ensuite, chaque changement la prévient individuellement.">
        <strong>Publication</strong>
        <span style={{ color: 'var(--text-muted)', flex: 1, minWidth: 200 }}>
          {liste === null ? '…' : actives.length
            ? `Publié : ${actives.map((p) => p.libelle).join(' · ')}`
            : 'Aucune période publiée — vos changements ne notifient personne.'}
        </span>
        <Bouton taille="sm" type="button" onClick={() => setVoirTout((v) => !v)} aria-expanded={voirTout}>
          {voirTout ? 'Masquer le détail' : 'Détail'}
        </Bouton>
        <Bouton taille="sm" type="button" onClick={() => setOuvert(true)}>Publier une période…</Bouton>
      </div>

      {!voirTout || liste === null ? null : montrees.length === 0 ? (
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>Aucune période publiée : tous les changements restent silencieux.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 4 }}>
          {montrees.map((p) => (
            <li key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 12.5, opacity: p.active ? 1 : 0.6 }}>
              <strong style={{ textTransform: 'capitalize' }}>{p.libelle}</strong>
              <span style={{ color: 'var(--text-muted)' }}>
                {p.par === 'amorcage'
                  ? 'déjà en vigueur'
                  : `publié le ${fr(p.le)} par ${p.parNom}${p.prevenus != null ? ` · ${p.prevenus} prévenu(s)` : ''}`}
                {!p.active && ` · retiré le ${fr(p.retireeLe || '')}${p.motifRetrait ? ` (${p.motifRetrait})` : ''}`}
              </span>
              {p.active && (
                <Bouton taille="sm" type="button" onClick={() => setRetrait({ id: p.id, motif: '' })} style={{ marginInlineStart: 'auto' }}>Retirer</Bouton>
              )}
            </li>
          ))}
        </ul>
      )}

      {ouvert && (
        <div role="dialog" aria-modal="true" aria-label="Publier le planning"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 80, display: 'grid', placeItems: 'center', padding: 16 }}>
          <div style={{ ...card, padding: 20, width: 'min(520px, 100%)', display: 'grid', gap: 12 }}>
            <strong style={{ fontSize: 15 }}>Publier le planning</strong>
            <div role="group" aria-label="Raccourcis" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {rac.map((x) => (
                <Bouton variante={du === x.du && au === x.au ? 'primaire' : 'contour'} taille="sm" type="button" key={x.cle} aria-pressed={du === x.du && au === x.au} onClick={() => { setDu(x.du); setAu(x.au); }}>{x.label}</Bouton>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <label style={{ display: 'grid', gap: 4, fontSize: 12.5, color: 'var(--text-muted)' }}>Du
                <input type="date" value={du} onChange={(e) => setDu(e.target.value)} style={inputStyle} />
              </label>
              <label style={{ display: 'grid', gap: 4, fontSize: 12.5, color: 'var(--text-muted)' }}>Au
                <input type="date" value={au} onChange={(e) => setAu(e.target.value)} style={inputStyle} />
              </label>
            </div>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
              Chaque personne ayant au moins une affectation du {du ? fr(du) : '…'} au {au ? fr(au) : '…'} recevra
              <strong> une notification</strong>. Les changements faits ensuite sur ces dates la préviendront un à un.
              {!ordreOk && du && au && <span style={{ color: 'var(--red-600)' }}> La date de fin précède celle de début.</span>}
            </p>
            <PiedActions taille="sm" annuler={{ onClick: () => setOuvert(false) }} principale={{ libelle: 'Publier et prévenir', libelleChargement: 'Publication…', chargement: envoi, disabled: !ordreOk || envoi, onClick: () => void publier() }} />
          </div>
        </div>
      )}

      {retrait && (
        <div role="dialog" aria-modal="true" aria-label="Retirer la publication"
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 80, display: 'grid', placeItems: 'center', padding: 16 }}>
          <div style={{ ...card, padding: 20, width: 'min(480px, 100%)', display: 'grid', gap: 12 }}>
            <strong style={{ fontSize: 15 }}>Retirer cette publication ?</strong>
            <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-muted)' }}>
              La période redevient silencieuse : vos changements ne préviendront plus personne, le temps de retravailler.
              Rien n’est effacé. Republiez ensuite pour prévenir à nouveau.
            </p>
            <label style={{ display: 'grid', gap: 4, fontSize: 12.5, color: 'var(--text-muted)' }}>Motif (5 caractères au moins)
              <input value={retrait.motif} autoFocus onChange={(e) => setRetrait({ ...retrait, motif: e.target.value })} style={inputStyle} />
            </label>
            <PiedActions taille="sm" annuler={{ onClick: () => setRetrait(null) }} principale={{ libelle: "Retirer", disabled: retrait.motif.trim().length < 5, onClick: () => void retirer() }} />
          </div>
        </div>
      )}
    </section>
  );
}
